<template>
  <control-wrapper
    v-bind="controlWrapper"
    :styles="styles"
    :is-focused="isFocused"
    :applied-options="appliedOptions"
  >
    <v-hover v-slot="{ isHovering }">
      <v-combobox
        :id="control.id + '-input'"
        v-disabled-icon-focus
        :class="styles.control.input"
        :disabled="!control.enabled"
        :autofocus="appliedOptions.focus"
        :placeholder="appliedOptions.placeholder"
        :label="computedLabel"
        :hint="control.description"
        :persistent-hint="persistentHint()"
        :required="control.required"
        :error-messages="control.errors"
        :clearable="isHovering"
        :model-value="control.data"
        :items="control.uischema.options?.suggestion"
        :item-title="(item) => t(item.label, item.label)"
        item-value="value"
        v-bind="vuetifyProps('v-select')"
        multiple
        density="compact"
        @update:model-value="onChange"
        @focus="isFocused = true"
        @blur="isFocused = false"
      >
        <template #selection="data">
          <v-chip
            :key="JSON.stringify(data.item)"
            v-bind="data.attrs"
            :model-value="data.selected"
            :disabled="data.disabled"
            size="small"
            style="
              margin-top: 2px;
              margin-bottom: var(--combobox-chips-margin-bottom);
            "
            @click:close="data.parent.selectItem(data.item)"
          >
            <template #prepend>
              <VIcon icon="tabler-x" @click="clearTag(data.item)" />
            </template>
            {{ data.item.title }}
          </v-chip>
        </template>
      </v-combobox>
    </v-hover>
  </control-wrapper>
</template>

<script lang="ts">
import {
  ControlElement,
  isPrimitiveArrayControl,
  JsonFormsRendererRegistryEntry,
  rankWith,
} from '@jsonforms/core';
import {
  rendererProps,
  RendererProps,
  useJsonFormsEnumControl,
} from '@jsonforms/vue';
import { defineComponent } from 'vue';
import { VHover } from 'vuetify/components';
import { useTranslator, useVuetifyControl } from '@/jsonForms/utils';
import { default as ControlWrapper } from './JsonFormsControlWrapper.vue';
import { DisabledIconFocus } from '@/jsonForms/directives';

const controlRenderer = defineComponent({
  name: 'EnumControlRenderer',
  components: {
    ControlWrapper,
    VHover,
  },
  directives: {
    DisabledIconFocus,
  },
  props: {
    ...rendererProps<ControlElement>(),
  },
  setup(props: RendererProps<ControlElement>) {
    const t = useTranslator();

    const control = useVuetifyControl(useJsonFormsEnumControl(props), (value) =>
      value !== null ? value : undefined
    );

    return { ...control, t };
  },
  methods: {
    clearTag(t: any) {
      this.onChange(this.control.data.filter((item: any) => item !== t.value));
    },
  },
});

export default controlRenderer;

export const entry: JsonFormsRendererRegistryEntry = {
  renderer: controlRenderer,
  tester: rankWith(2, isPrimitiveArrayControl),
};
</script>
